import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { MapContainer } from "react-leaflet";
import GeotabApi from "mg-api-js";
//--------------------------------------------------------------------------------------------
import Resize from "src/components/common/resize";
import { useMyStore } from "src/components/main/mainStore";
import {
    MyFormData, MyDate, 
    myAxios, scroll, getTimeStamp,
    appTitle, GeotabApiAuthentication, GeotabApiOptions, safeParseJson
} from "src/components/common/functions";
import Maps from "./map";
//--------------------------------------------------------------------------------------------
export default function LoadMaps({ tabTitle }){
    const mapFull = useMyStore(state => state.mapFull);
    const user = useMyStore(state => state.user);
    const mapWorkLogs = useMyStore(state => state.mapWorkLogs);
    const setMapLogs = useMyStore(state => state.setMapLogs);
    const trucks = useMyStore(state => state.mapTrucks);
    const setTrucks = useMyStore(state => state.setMapTrucks);
    //--------------------------------------------------------------------------------------------
    const history  = useHistory();
    const path = history.location.pathname;
    const path1 = decodeURIComponent(path.split("/")[1] ?? ""); // title
    const path2 = decodeURIComponent(path.split("/")[2] ?? ""); // hash | "Add"
    //--------------------------------------------------------------------------------------------
    const [updateDateTime, setUpdateDateTime] = useState({
        "Map Work Logs": mapWorkLogs.updatedAt === null?getTimeStamp():mapWorkLogs.updatedAt, 
        "Trucks": trucks.updatedAt === null?getTimeStamp():trucks.updatedAt, 
    });
    //--------------------------------------------------------------------------------------------
    const [showLoading, setShowLoading] = useState(false);
    const [userSelectedDate, setUserSelectedDate] = useState((new MyDate()).s());
    const workInstructions = useMemo(() => (mapWorkLogs[userSelectedDate] ?? [])
    .filter((value, index, self) => index === self.findIndex(
        (t) => (t.workInstructionHashKey === value.workInstructionHashKey)
    ))
    .map(w =>({
        ...w, 
        workInstructionFiles: safeParseJson(w.workInstructionFiles)
    })).map((w => ({
        ...w, 
        projectFiles: safeParseJson(w.projectFiles),
        projectName: `${w.projectNumber} - ${w.organizationName} - ${w.clientProjectNumber}`
    }))) || [], [mapWorkLogs, userSelectedDate]);
    const workLogs = useMemo(() => (mapWorkLogs[userSelectedDate] ?? []).map(w => ({
        email: w.labor, 
        name: w.userName, 
        hashKey: w.workInstructionHashKey
    })), [mapWorkLogs, userSelectedDate]);
    //--------------------------------------------------------------------------------------------
    useEffect(() => {
        let isMounted = true;
        const getData = async () => {            
            const api = new GeotabApi(GeotabApiAuthentication, GeotabApiOptions);
            //------------------------------------------------------
            const calls = [
                ['Get', {
                typeName: 'Device',
                // "search": {
                //     "name": "131"
                // }
                }],
                ['Get', {
                    typeName: 'DeviceStatusInfo',
                    // search: {
                    //     deviceSearch: { id: "b2F" },
                    //   },
                }],
                ['Get', {
                    typeName: 'StatusData',
                    resultsLimit: 100,
                    "search": {
                        diagnosticSearch: { id: 
                            "DiagnosticOdometerId" || //meter to mi
                            "DiagnosticEngineSpeedId" || 
                            "DiagnosticFuelUnitsId" || 
                            'DiagnosticFuelLevelId'
                        },
                        "deviceSearch": {
                            "id": "b2F"
                        },
                        fromDate: "2023-01-24T18:20:00.000Z"
                    }
                }]
            ];
            const res = await api.multiCall(calls);
            //---------------
            if(!isMounted) return;
            const result = res.data.result;
            const eventData = Object.fromEntries(result[1].map(r => [r.device.id, r]));
            const data = result[0].map(r => ({...r, eventData: eventData[r.id]}));
            setTrucks(data, updateDateTime["Trucks"]);
            //------------------------------------------------------
        }
        if(updateDateTime["Trucks"] !== trucks.updatedAt)  getData();
        return () => {isMounted = false}  
    }, [updateDateTime, setTrucks, trucks.updatedAt])
    //--------------------------------------------------------------------------------------------
    const prev = useRef({ userSelectedDate });
    useEffect(() => {
        if(updateDateTime["Map Work Logs"] === mapWorkLogs.updatedAt && prev.current.userSelectedDate === userSelectedDate) return;
        scroll(null);
        let isMounted = true;
        const controller = new AbortController();
        const getData = async () => {
            const formData = new MyFormData();
            formData.append("selectedDate", userSelectedDate);
            //---------------------------
            setShowLoading(true);
            const resWl = await myAxios("maps/readWorkInstructions.php", formData, undefined, controller.signal);
            if(!isMounted) return;
            setShowLoading(false);
            if(!resWl.data) return;
            //---------------------------
            setMapLogs(userSelectedDate, resWl.data, updateDateTime["Map Work Logs"]);
            prev.current = { userSelectedDate };
        }
        if(!mapWorkLogs[userSelectedDate] || (updateDateTime["Map Work Logs"] !== mapWorkLogs.updatedAt)) getData();
        return () => {
            isMounted = false;
            controller.abort();
        }  
    }, [userSelectedDate, mapWorkLogs, updateDateTime, setMapLogs])
    useEffect(() => {
        document.title = tabTitle;
        return () => {
            document.title = appTitle;
        };
    }, [tabTitle]);
    //--------------------------------------------------------------------------------------------
    return(
        <div className = "cssTab">
            <div className = "cssHeader" style = {{background: user.userTheme}}>
                <span>
                    <Resize/>
                    Map
                    <button
                        type = "button"
                        data-toggle = "tooltip"
                        title = {`Refreshed on ${Object.values(updateDateTime)[0]}.`}
                        className = "btn btn-primary ml-3"
                        onClick = {() => {
                            const timeStamp = getTimeStamp();
                            setUpdateDateTime(Object.fromEntries(Object.keys(updateDateTime).map(key => [key, timeStamp])));
                        }}
                    >
                        ↻
                    </button>
                </span>
                <input
                    className = "form-control w-auto mr-3"
                    type = "date"
                    min = "1900-01-01"
                    max = "2999-12-31"
                    value = {userSelectedDate}
                    onChange = {e => setUserSelectedDate(e.currentTarget.value)}
                />
            </div>
            {showLoading?<div className = "h5 p-3 text-center">Loading...</div>:<div className = "mapHeight">
                <MapContainer center = {user.coords} zoom = {12} scrollWheelZoom = {true} zoomControl = {false}>
                    <Maps
                        geoData = {trucks.data}
                        mapFull = {mapFull}
                        workInstructions = {workInstructions} 
                        workLogs = {workLogs}
                        history = {history}
                        path1 = {path1}
                        path2 = {path2}
                    />
                </MapContainer>
            </div>}
        </div>
    );
}




